import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import maplibregl from 'maplibre-gl';
import { Cpu, Download, Loader2, MapPin, Play, Square, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { recordEvent } from '@/store/appEvents';
import { authHeader } from '@/store/auth';
import { hubFetch } from '@/lib/hubUrl';
import { loadJson } from '@/lib/persist';
import { useMapBase } from '@/map/useMapBase';
import { useSettings } from '@/store/settings';
import { cn } from '@/lib/utils';
import { rootRoute } from './__root';

/* Wire shapes — mirror crates/sitl-manager (serde snake_case). */
interface SitlCapability {
  backend: string;
  platform_key: string;
  install:
    | { state: 'not_installed' }
    | { state: 'installing'; progress_pct: number; downloaded_bytes: number; total_bytes: number }
    | { state: 'installed'; version: string; installed_ts_ms: number; path: string }
    | { state: 'update_available'; current: string; available: string; path: string }
    | { state: 'failed'; reason: string };
  external_binary: string | null;
  mirror_url: string;
}

interface SitlSummary {
  sysid: number;
  vehicle: string;
  home: [number, number, number];
  started_ts_ms: number;
  pid: number;
  log_path: string;
  status: 'running' | 'stopped' | 'crashed';
}

const POLL_MS = 5000;

type Formation = 'line' | 'grid' | 'ring';

/** Per-drone home offsets (metres east/north of the anchor) for a formation. */
function formationOffsets(formation: Formation, n: number, spacing: number): [number, number][] {
  if (n <= 1) return [[0, 0]];
  switch (formation) {
    case 'line':
      return Array.from({ length: n }, (_, i) => [i * spacing, 0]);
    case 'grid': {
      const side = Math.ceil(Math.sqrt(n));
      return Array.from({ length: n }, (_, i) => [
        (i % side) * spacing,
        -Math.floor(i / side) * spacing,
      ]);
    }
    case 'ring': {
      // Radius chosen so neighbours sit `spacing` apart along the chord.
      const r = spacing / (2 * Math.sin(Math.PI / n));
      return Array.from({ length: n }, (_, i) => {
        const a = (2 * Math.PI * i) / n;
        return [r * Math.cos(a), r * Math.sin(a)];
      });
    }
  }
}

/** Offset a lat/lon anchor by metres east (dx) / north (dy). */
function offsetLatLon(lat: number, lon: number, dx: number, dy: number): [number, number] {
  const mPerDegLat = 111_320;
  return [lat + dy / mPerDegLat, lon + dx / (mPerDegLat * Math.cos((lat * Math.PI) / 180))];
}

/** Where new drones spawn when no pin is set: the operator's last map view. */
function viewCentre(settingsLat: number, settingsLon: number): { lat: number; lon: number } {
  const cam = loadJson<{ center?: [number, number] }>('fabricos.mapCamera.v1', {});
  if (cam.center) return { lat: cam.center[1], lon: cam.center[0] };
  return { lat: settingsLat, lon: settingsLon };
}

/**
 * SITL workspace — Pilot-style layout: control rail on the left, full-bleed
 * map on the right. Click the map to pin where new simulated drones spawn;
 * running instances list in the rail with kill buttons. Real drones live in
 * Assets; sims here.
 */
function SitlWorkspace() {
  const [cap, setCap] = useState<SitlCapability | null>(null);
  const [instances, setInstances] = useState<SitlSummary[]>([]);
  const [count, setCount] = useState(1);
  const [formation, setFormation] = useState<Formation>('line');
  const [spacing, setSpacing] = useState(5);
  /** Optional MAVLink target override — '' = hub default (usually 14550). */
  const [target, setTarget] = useState('');
  /** Spawn location pinned on the map / typed in. Null = map view centre. */
  const [spawnPoint, setSpawnPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialLat = useSettings((s) => s.map.initialLat);
  const initialLon = useSettings((s) => s.map.initialLon);

  const refresh = useCallback(async () => {
    try {
      const [capRes, listRes] = await Promise.all([
        hubFetch('/api/sitl/capability'),
        hubFetch('/api/sitl'),
      ]);
      if (capRes.ok) setCap(await capRes.json());
      if (listRes.ok) setInstances(await listRes.json());
      setError(null);
    } catch {
      setError('Hub unreachable');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function act(label: string, path: string, init: RequestInit) {
    setBusy(label);
    setError(null);
    try {
      const res = await hubFetch(path, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...authHeader(), ...init.headers },
      });
      if (!res.ok) {
        setError(`${label} failed: ${(await res.text()).slice(0, 140)}`);
        recordEvent('sitl', `${label.toLowerCase()} failed`, { severity: 'warning' });
      } else {
        recordEvent('sitl', label.toLowerCase());
      }
      await refresh();
    } catch {
      setError(`${label} failed: Hub unreachable`);
    } finally {
      setBusy(null);
    }
  }

  async function spawn() {
    const anchor = spawnPoint ?? viewCentre(initialLat, initialLon);
    const sysidStart = instances.reduce((max, i) => Math.max(max, i.sysid), 0) + 1;
    // "14551" shorthand → loopback; full "host:port" passes through. Empty →
    // omit the field so the Hub uses its global mavlink_target (port 14550).
    const trimmed = target.trim();
    const mavlinkTarget =
      trimmed === '' ? undefined : /^\d+$/.test(trimmed) ? `127.0.0.1:${trimmed}` : trimmed;
    if (mavlinkTarget && !/^[\w.-]+:\d{1,5}$/.test(mavlinkTarget)) {
      setError(`Invalid MAVLink target "${trimmed}" — use a port (14551) or host:port.`);
      return;
    }
    setBusy('Spawn');
    setError(null);
    try {
      // The Hub natively lays multiple drones out in an east line; grid and
      // ring are composed client-side as one spawn per drone with a computed
      // home. Line with n>1 stays a single request (backend-native).
      const requests: { sysid_start: number; count: number; lat: number; lon: number }[] = [];
      if (formation === 'line') {
        requests.push({ sysid_start: sysidStart, count, lat: anchor.lat, lon: anchor.lon });
      } else {
        formationOffsets(formation, count, spacing).forEach(([dx, dy], i) => {
          const [lat, lon] = offsetLatLon(anchor.lat, anchor.lon, dx, dy);
          requests.push({ sysid_start: sysidStart + i, count: 1, lat, lon });
        });
      }
      for (const r of requests) {
        const res = await hubFetch('/api/sitl', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            count: r.count,
            sysid_start: r.sysid_start,
            home_lat: r.lat,
            home_lon: r.lon,
            spacing_m: spacing,
            ...(mavlinkTarget ? { mavlink_target: mavlinkTarget } : {}),
          }),
        });
        if (!res.ok) {
          setError(`Spawn failed: ${(await res.text()).slice(0, 140)}`);
          recordEvent('sitl', 'spawn failed', { severity: 'warning' });
          break;
        }
      }
      recordEvent(
        'sitl',
        count > 1 ? `spawned ${count} drones (${formation})` : 'spawned 1 drone',
      );
      await refresh();
    } catch {
      setError('Spawn failed: Hub unreachable');
    } finally {
      setBusy(null);
    }
  }

  const running = instances.filter((i) => i.status === 'running').length;
  const install = cap?.install;
  const usable =
    !!cap &&
    (cap.external_binary != null ||
      install?.state === 'installed' ||
      install?.state === 'update_available');

  return (
    <div className="flex h-full bg-base">
      {/* Control rail */}
      <aside
        className="flex h-full flex-none flex-col border-r border-border-default bg-surface-1"
        style={{ width: 'var(--rail-left)' }}
      >
        <header className="flex items-center gap-2 border-b border-border-default px-3 py-3">
          <Cpu className="h-4 w-4 text-accent" />
          <span className="font-mono text-[13px] font-semibold tracking-[0.04em] text-fg-primary">
            SITL
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-fg-muted">simulator</span>
          <span className="ml-auto">
            {cap && <InstallBadge install={cap.install} external={cap.external_binary} />}
          </span>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden p-3">
          {/* Install nudge — only when spawning is impossible. */}
          {cap && !usable && (
            <div className="space-y-2 rounded border border-status-warn/40 bg-status-warn/5 p-2.5">
              <p className="text-[11px] leading-snug text-fg-secondary">
                {install?.state === 'installing'
                  ? `Downloading ArduCopter SITL… ${install.progress_pct}%`
                  : install?.state === 'failed'
                    ? `Install failed: ${install.reason}`
                    : 'No SITL binary on the Hub machine. Install one, or restart the Hub with FABRICOS_SITL_BINARY pointing at a local arducopter build.'}
              </p>
              {install?.state === 'installing' ? (
                <div className="h-1 overflow-hidden rounded bg-surface-3">
                  <div
                    className="h-full bg-status-warn transition-[width]"
                    style={{ width: `${install.progress_pct}%` }}
                  />
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy != null}
                  className="w-full"
                  onClick={() =>
                    void act('Install', '/api/sitl/install', { method: 'POST', body: '{}' })
                  }
                >
                  {busy === 'Install' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Install ArduCopter SITL
                </Button>
              )}
            </div>
          )}

          {/* Spawn form */}
          <section className="space-y-2">
            <SectionLabel>Spawn</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Drones">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={count}
                  onChange={(e) => setCount(Math.min(20, Math.max(1, Number(e.target.value) || 1)))}
                  className={inputCls}
                />
              </Field>
              <Field label="Port" hint="blank = 14550">
                <input
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="14550"
                  title="MAVLink target — a port (14551) or host:port. Blank = hub default. The hub needs a listener on that port."
                  className={inputCls}
                />
              </Field>
            </div>

            {count > 1 && (
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <Field label="Formation">
                  <div className="grid h-8 grid-cols-3 overflow-hidden rounded border border-border-default">
                    {(['line', 'grid', 'ring'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setFormation(f)}
                        className={cn(
                          'font-mono text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors',
                          formation === f
                            ? 'bg-accent/15 text-accent'
                            : 'bg-base text-fg-muted hover:bg-surface-2 hover:text-fg-secondary',
                        )}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Spacing" hint="m">
                  <input
                    type="number"
                    min={2}
                    max={200}
                    value={spacing}
                    onChange={(e) =>
                      setSpacing(Math.min(200, Math.max(2, Number(e.target.value) || 5)))
                    }
                    className={cn(inputCls, 'w-16')}
                  />
                </Field>
              </div>
            )}

            <Field label="Location" hint={spawnPoint ? 'pinned' : 'map view centre'}>
              <div className="flex items-center gap-1.5">
                <MapPin
                  className={cn('h-3.5 w-3.5 flex-none', spawnPoint ? 'text-accent' : 'text-fg-muted')}
                />
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-fg-secondary">
                  {spawnPoint
                    ? `${spawnPoint.lat.toFixed(5)}, ${spawnPoint.lon.toFixed(5)}`
                    : 'click the map to pin'}
                </span>
                {spawnPoint && (
                  <button
                    type="button"
                    onClick={() => setSpawnPoint(null)}
                    title="Clear pin — spawn at the map view centre"
                    className="grid h-5 w-5 flex-none place-items-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-primary"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </Field>

            <Button
              size="sm"
              variant="primary"
              disabled={!usable || busy != null}
              onClick={spawn}
              className="w-full !h-9"
            >
              {busy === 'Spawn' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              Spawn {count > 1 ? `${count} drones` : 'drone'}
            </Button>
          </section>

          {/* Running instances */}
          <section className="space-y-2">
            <SectionLabel>
              Running
              {running > 0 && <span className="ml-1.5 text-fg-secondary">{running}</span>}
            </SectionLabel>
            {instances.length === 0 ? (
              <p className="rounded border border-dashed border-border-default px-2.5 py-4 text-center text-[10.5px] leading-relaxed text-fg-muted">
                No simulated drones.
                <br />
                Spawned drones join the fleet on every page.
              </p>
            ) : (
              <>
                <ul className="space-y-1">
                  {instances.map((i) => (
                    <li
                      key={i.sysid}
                      className="group flex items-center gap-2 rounded border border-border-subtle bg-surface-2/40 px-2 py-1.5"
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 flex-none rounded-full',
                          i.status === 'running' && 'bg-status-good',
                          i.status === 'crashed' && 'bg-status-critical',
                          i.status === 'stopped' && 'bg-surface-3',
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[11.5px] text-fg-primary">
                          sysid {i.sysid}
                          <span className="ml-1.5 text-[9.5px] uppercase text-fg-muted">
                            {i.vehicle}
                          </span>
                        </div>
                        <div className="truncate font-mono text-[9.5px] text-fg-muted">
                          {i.home[0].toFixed(4)}, {i.home[1].toFixed(4)} · {fmtUptime(i.started_ts_ms)}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={busy != null}
                        onClick={() =>
                          void act(`Kill ${i.sysid}`, `/api/sitl/${i.sysid}`, { method: 'DELETE' })
                        }
                        title={`Kill sysid ${i.sysid}`}
                        className="grid h-6 w-6 flex-none place-items-center rounded text-fg-muted opacity-0 transition-opacity hover:bg-surface-3 hover:text-status-critical group-hover:opacity-100"
                      >
                        <Square className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy != null}
                  onClick={() => void act('Kill all', '/api/sitl', { method: 'DELETE' })}
                  className="w-full"
                >
                  <Square className="h-3.5 w-3.5" />
                  Kill all
                </Button>
              </>
            )}
          </section>

          {error && (
            <p className="rounded border border-status-critical/40 bg-status-critical/10 px-2.5 py-2 text-[11px] leading-snug text-status-critical">
              {error}
            </p>
          )}
        </div>
      </aside>

      {/* Map — click to pin the spawn location. */}
      <section className="relative flex-1 overflow-hidden">
        <SpawnMap
          point={spawnPoint}
          preview={
            spawnPoint
              ? formationOffsets(formation, count, spacing).map(([dx, dy]) => {
                  const [lat, lon] = offsetLatLon(spawnPoint.lat, spawnPoint.lon, dx, dy);
                  return { lat, lon };
                })
              : []
          }
          instances={instances}
          onPick={setSpawnPoint}
        />
        <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center px-3">
          <div className="flex items-center gap-2 rounded-sm border border-border-default bg-base/85 px-3 py-1.5 font-mono text-[10.5px] text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur-md">
            <span
              className="font-semibold uppercase tracking-[0.16em]"
              style={{ color: 'var(--color-accent)' }}
            >
              Spawn
            </span>
            <span className="hidden h-3 w-px bg-border-default sm:inline" aria-hidden />
            <span>
              <kbd className="mr-1 inline-block rounded border border-border-default bg-surface-2 px-1.5 py-[1px] text-[10px] text-fg-primary">
                Click
              </kbd>
              = set spawn point
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: 'var(--color-accent)' }}
      >
        {children}
      </span>
      <span className="h-px flex-1" style={{ background: 'var(--color-accent-edge)' }} />
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-1.5">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-fg-muted">
          {label}
        </span>
        {hint && <span className="text-[9px] text-fg-disabled">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  'h-8 w-full rounded border border-border-default bg-base px-2 text-center font-mono text-[12px] text-fg-primary placeholder:text-fg-disabled outline-none focus:border-accent';

function SpawnMap({
  point,
  preview,
  instances,
  onPick,
}: {
  point: { lat: number; lon: number } | null;
  /** Where each drone of the pending spawn will land (formation preview). */
  preview: { lat: number; lon: number }[];
  instances: SitlSummary[];
  onPick: (p: { lat: number; lon: number }) => void;
}) {
  const pinRef = useRef<maplibregl.Marker | null>(null);
  const previewRef = useRef<maplibregl.Marker[]>([]);
  const homeMarkersRef = useRef<Map<number, maplibregl.Marker>>(new Map());
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const { containerRef, mapRef } = useMapBase({
    showScale: true,
    onReady: (m) => {
      m.on('click', (e) => onPickRef.current({ lat: e.lngLat.lat, lon: e.lngLat.lng }));
    },
  });

  // Spawn pin.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (!point) {
      pinRef.current?.remove();
      pinRef.current = null;
      return;
    }
    if (!pinRef.current) {
      const el = document.createElement('div');
      el.style.cssText = [
        'width:16px',
        'height:16px',
        'border-radius:50%',
        'border:2px solid var(--color-accent)',
        'background:rgba(14,15,17,0.75)',
        'box-shadow:0 0 10px 2px rgba(79,200,224,0.35)',
        'box-sizing:border-box',
      ].join(';');
      pinRef.current = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([point.lon, point.lat])
        .addTo(m);
    }
    pinRef.current.setLngLat([point.lon, point.lat]);
  }, [point, mapRef]);

  // Formation preview — faint dots where each pending drone will land.
  // ≤20 markers, so tear-down + rebuild per change is fine.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    previewRef.current.forEach((mk) => mk.remove());
    previewRef.current = preview.map((p) => {
      const el = document.createElement('div');
      el.style.cssText = [
        'width:8px',
        'height:8px',
        'border-radius:50%',
        'border:1px solid var(--color-accent)',
        'background:rgba(79,200,224,0.25)',
        'box-sizing:border-box',
        'pointer-events:none',
      ].join(';');
      return new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([p.lon, p.lat])
        .addTo(m);
    });
    return () => {
      previewRef.current.forEach((mk) => mk.remove());
      previewRef.current = [];
    };
  }, [preview, mapRef]);

  // Home markers for running instances — small labelled dots so the operator
  // sees where the sims live relative to the next spawn.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const markers = homeMarkersRef.current;
    const seen = new Set<number>();
    for (const i of instances) {
      seen.add(i.sysid);
      let mk = markers.get(i.sysid);
      if (!mk) {
        const el = document.createElement('div');
        el.style.cssText = [
          'display:flex',
          'align-items:center',
          'gap:4px',
          'padding:1px 6px 1px 4px',
          'border-radius:9999px',
          'border:1px solid var(--color-border-default)',
          'background:rgba(14,15,17,0.8)',
          'font-family:var(--font-mono)',
          'font-size:9px',
          'color:var(--color-fg-secondary)',
          'pointer-events:none',
          'white-space:nowrap',
        ].join(';');
        const dot = document.createElement('span');
        dot.style.cssText =
          'width:6px;height:6px;border-radius:50%;background:var(--color-status-good)';
        const label = document.createElement('span');
        label.textContent = `sysid ${i.sysid}`;
        el.appendChild(dot);
        el.appendChild(label);
        mk = new maplibregl.Marker({ element: el, anchor: 'bottom', offset: [0, -6] })
          .setLngLat([i.home[1], i.home[0]])
          .addTo(m);
        markers.set(i.sysid, mk);
      }
      mk.setLngLat([i.home[1], i.home[0]]);
    }
    for (const sysid of Array.from(markers.keys())) {
      if (!seen.has(sysid)) {
        markers.get(sysid)?.remove();
        markers.delete(sysid);
      }
    }
  }, [instances, mapRef]);

  useEffect(() => {
    const markers = homeMarkersRef.current;
    return () => {
      markers.forEach((mk) => mk.remove());
      markers.clear();
    };
  }, []);

  return <div ref={containerRef} className="h-full w-full cursor-crosshair" />;
}

function InstallBadge({
  install,
  external,
}: {
  install: SitlCapability['install'];
  external: string | null;
}) {
  if (external) return <Badge tone="good">system binary</Badge>;
  switch (install.state) {
    case 'installed':
      return <Badge tone="good">v{install.version}</Badge>;
    case 'update_available':
      return <Badge tone="warn">v{install.current} · update</Badge>;
    case 'installing':
      return <Badge tone="accent">installing {install.progress_pct}%</Badge>;
    case 'failed':
      return <Badge tone="critical">install failed</Badge>;
    default:
      return <Badge tone="default">not installed</Badge>;
  }
}

function fmtUptime(startedMs: number): string {
  const s = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

export const sitlRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sitl',
  component: SitlWorkspace,
});
